import React, { FormEvent, useState } from 'react'
import { Cell, Form } from 'react-aria-components'
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'

import { FoodGroup, SearchIngredient } from '@/api/types'
import {
  Column,
  Row,
  Table,
  TableBody,
  TableHeader,
  TablePagination,
  usePagination,
} from '@/components/Table'
import { Button } from '@/components/Button'
import { PageEditHeader } from '@/components/PageEditHeader'
import { TextField } from '@/components/TextField'
import { Label } from '@/components/Label'
import FCDBIngredientSelector from '../ingredients/FCDBIngredientSelector'

type Props = {
  rows: SearchIngredient[]
  foodGroup?: FoodGroup
  onAddIngredients: (ings: SearchIngredient[]) => void
  onSaveGroup: (name: string) => void
  onDeleteGroup?: () => void
  onDeleteIngredient: (id: number) => void
}

export default function EditFoodGroup({
  rows,
  foodGroup,
  onAddIngredients,
  onSaveGroup,
  onDeleteGroup,
  onDeleteIngredient,
}: Props) {
  const [pagination, getPageRows] = usePagination(rows)
  const { t } = useTranslation()
  const [tempName, setTempName] = useState(foodGroup?.name ?? '')
  const isNew = !foodGroup

  const handleSubmit = (ev: FormEvent) => {
    ev.preventDefault()
    onSaveGroup(tempName)
  }

  return (
    <Form onSubmit={handleSubmit}>
      <PageEditHeader title={t('foodGroups.title')}>
        {!isNew && (
          <Button variant="negative" onPress={onDeleteGroup}>
            {t('foodGroups.deleteGroup')}
          </Button>
        )}
        <Button variant="primary" type="submit">
          {t('foodGroups.saveGroup')}
        </Button>
      </PageEditHeader>

      <div className="panel">
        <TextField
          isRequired
          label={t('foodGroups.name')}
          value={tempName}
          errorMessage={t('ingredient.emptyName')}
          onChange={(value) => setTempName(value)}
        />

        <div>
          <div className="EditIngredientGroup-add">
            <Label>{t('ingredient.fcdbIngredients')}</Label>
            <FCDBIngredientSelector selection={rows} onChangeSelection={onAddIngredients} />
          </div>

          <Table aria-label="Food Groups" style={{ padding: 0 }}>
            <TableHeader>
              <Column id="name" isRowHeader>
                Name
              </Column>
              <Column id="delete" width={40} />
            </TableHeader>
            <TableBody items={getPageRows()}>
              {(row: SearchIngredient) => (
                <Row id={row.idIngredient}>
                  <Cell>{row.name}</Cell>

                  <Cell>
                    <Button variant="icon" onPress={() => onDeleteIngredient(row.idIngredient)}>
                      <FontAwesomeIcon icon={faTrashAlt} color="var(--invalid-color)" />
                    </Button>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
          <TablePagination pagination={pagination} />
        </div>
      </div>
    </Form>
  )
}
